import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../App';
import { useSortable, SortTh, useIsMobile } from '../utils';
import PasswordField from '../components/PasswordField';
import { HardDeleteClientModal } from './HardDeleteClientModal';

function clientStatus(c) {
  const pct = c.storage_used_gb / (c.plan?.included_storage_gb || 1);
  return pct > 0.85 ? 'alert' : 'ok';
}

function Badge({ bg, color, label }) {
  return <span style={{ display: 'inline-block', padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)', letterSpacing: 1, textTransform: 'uppercase', borderRadius: 2, background: bg, color }}>{label}</span>;
}

// #210 — el «propio» del usuario reseller es su empresa (me.client_id): badge PROPIO.
// Para el admin, las empresas reseller llevan el badge RESELLER.
function ClientBadge({ client, myCompanyId }) {
  const base = { padding: '1px 6px', borderRadius: 4, fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 1 };
  if (myCompanyId && client.id === myCompanyId) {
    return <span title="Tu empresa (tu infraestructura)" style={{ ...base, background: 'rgba(0,212,255,0.12)', color: 'var(--accent)', border: '1px solid var(--accent)' }}>PROPIO</span>;
  }
  if (client.is_reseller && !myCompanyId) {
    return <span title="Empresa reseller" style={{ ...base, background: 'rgba(0,229,160,0.12)', color: 'var(--green)', border: '1px solid var(--green)' }}>RESELLER</span>;
  }
  return null;
}

// #210 — `canCreateNormal`: puede crear clientes (admin, operador, o reseller con can_create_clients).
// Sólo el admin marca «Es reseller» (empresa reseller con prefijo, descuento y cuota).
export function NewClientModal({ plans, resellers, isAdmin, onClose, onCreated, initialReseller = false }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ name: '', cuit: '', contact_name: '', contact_email: '', contact_phone: '', city: '', address: '', plan_id: plans[0]?.id || '', reseller_id: '', initial_user_email: '', initial_user_password: '', send_welcome: false, is_reseller: !!initialReseller, reseller_bucket_prefix: '', reseller_discount_pct: '', reseller_storage_quota_gb: '' });
  const [loading, setLoading]     = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isCompany = isAdmin && form.is_reseller;

  const submit = async () => {
    setSubmitted(true);
    if (!form.name?.trim()) { setError('La razón social es obligatoria'); return; }
    if (!form.contact_name?.trim()) { setError('El nombre de contacto es obligatorio'); return; }
    if (!form.contact_email?.trim()) { setError('El email de contacto es obligatorio'); return; }
    if (!form.plan_id && !isCompany) { setError('Debe seleccionar un plan'); return; }
    setError('');
    setLoading(true);
    try {
      const payload = { ...form, plan_id: form.plan_id || null, reseller_id: isCompany ? null : (form.reseller_id || null) };
      if (isCompany) {
        payload.reseller_bucket_prefix = form.reseller_bucket_prefix.trim() || null;
        payload.reseller_discount_pct = form.reseller_discount_pct === '' ? null : parseFloat(form.reseller_discount_pct);
        payload.reseller_storage_quota_gb = form.reseller_storage_quota_gb === '' ? null : parseFloat(form.reseller_storage_quota_gb);
      } else {
        delete payload.is_reseller;
        delete payload.reseller_bucket_prefix;
        delete payload.reseller_discount_pct;
        delete payload.reseller_storage_quota_gb;
      }
      if (isCompany || !form.initial_user_email) {
        delete payload.initial_user_email;
        delete payload.initial_user_password;
        delete payload.send_welcome;
      }
      await api.createClient(payload);
      onCreated();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // [label, key, type, placeholder, required]
  const fields = [
    [['Razón Social', 'name',          'text',  'Empresa S.A.',          true],  ['CUIT',  'cuit',          'text',  '30-00000000-0',          false]],
    [['Contacto',     'contact_name',  'text',  'Nombre Apellido',       true],  ['Email', 'contact_email', 'email', 'contacto@empresa.com',   true]],
    [['Teléfono',     'contact_phone', 'text',  '+54 9 11...',           false], ['Ciudad','city',          'text',  'Buenos Aires',           false]],
    [['Dirección',    'address',       'text',  'Av. Corrientes 1234',   false], null],
  ];

  const inputBorder = (key, required) =>
    submitted && required && !form[key]?.trim()
      ? '1px solid var(--red)'
      : '1px solid var(--border)';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: isMobile ? '95vw' : 520, maxHeight: '90vh', overflowY: isMobile ? 'auto' : undefined, display: 'flex', flexDirection: 'column', clipPath: isMobile ? 'none' : 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>NUEVO CLIENTE</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
          {fields.map((row, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: row[1] ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 16 }}>
              {row.filter(Boolean).map(([label, key, type, placeholder, required]) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                    {label}{required && <span style={{ color: 'var(--red)', marginLeft: 2 }}>*</span>}
                  </label>
                  <input type={type} placeholder={placeholder} value={form[key]} onChange={e => set(key, e.target.value)}
                    style={{ width: '100%', background: 'var(--bg)', border: inputBorder(key, required), color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
                </div>
              ))}
            </div>
          ))}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
              Plan {!isCompany && <span style={{ color: 'var(--red)' }}>*</span>}
            </label>
            <select value={form.plan_id} onChange={e => set('plan_id', e.target.value)}
              style={{ width: '100%', background: 'var(--bg)', border: submitted && !form.plan_id && !isCompany ? '1px solid var(--red)' : '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
              {isCompany && <option value="">Sin plan (sin infraestructura propia)</option>}
              {!isCompany && !form.plan_id && <option value="" disabled>Seleccioná un plan</option>}
              {plans.map(p => <option key={p.id} value={p.id}>{p.name}{p.base_price_usd != null ? ` — $${p.base_price_usd} USD/mes` : ''}</option>)}
            </select>
          </div>
          {isAdmin && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_reseller}
                onChange={e => set('is_reseller', e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
              <span style={{ fontSize: 12, fontFamily: 'var(--mono)' }}>
                Es reseller
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  Empresa reseller: tiene clientes y usuarios reseller propios. Su infraestructura es este mismo cliente. No lleva usuario inicial ni reseller.
                </span>
              </span>
            </label>
          )}

          {isCompany && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              {[['Prefijo de buckets', 'reseller_bucket_prefix', 'text', 'ej. acme'],
                ['Descuento (%)', 'reseller_discount_pct', 'number', '0'],
                ['Cuota del reseller (GB)', 'reseller_storage_quota_gb', 'number', 'sin tope']].map(([label, key, type, ph]) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>{label}</label>
                  <input type={type} min={type === 'number' ? 0 : undefined} placeholder={ph} value={form[key]}
                    onChange={e => set(key, key === 'reseller_bucket_prefix' ? e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') : e.target.value)}
                    style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
                </div>
              ))}
            </div>
          )}

          {isAdmin && !isCompany && resellers.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Reseller (opcional)</label>
              <select value={form.reseller_id} onChange={e => set('reseller_id', e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
                <option value="">— Sin reseller —</option>
                {resellers.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}

          {/* USUARIO INICIAL */}
          {!isCompany && <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 4 }}>Usuario Inicial</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>Opcional — crea acceso de cliente automáticamente</div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Email de acceso</label>
              <input type="email" placeholder="usuario@empresa.com" value={form.initial_user_email} onChange={e => set('initial_user_email', e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
            </div>
            <PasswordField
              value={form.initial_user_password}
              onChange={v => set('initial_user_password', v)}
              label="Contraseña"
              optional={true}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div
                onClick={() => set('send_welcome', !form.send_welcome)}
                style={{ width: 36, height: 20, borderRadius: 10, background: form.send_welcome ? 'var(--accent)' : 'var(--border)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
              >
                <div style={{ position: 'absolute', top: 3, left: form.send_welcome ? 19 : 3, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
              </div>
              <span style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>Enviar email de bienvenida</span>
            </div>
          </div>}

          <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--red)' }}>*</span> Campos obligatorios
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
          <button onClick={submit} disabled={loading} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>
            {loading ? 'CREANDO...' : 'CREAR CLIENTE →'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Clients() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { me, isAdmin, isReseller, resellerPerms } = useAuth();
  const canCreateNormal = isAdmin || (!isReseller) || !!resellerPerms?.can_create_clients;
  const [clients, setClients]   = useState([]);
  const [plans, setPlans]       = useState([]);
  const [resellers, setResellers] = useState([]);
  const [modal, setModal]       = useState(false);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resellerModal, setResellerModal] = useState(null);
  const [selectedReseller, setSelectedReseller] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [deletingClient, setDeletingClient] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);
  const [toast, setToast] = useState({ msg: '', type: '' });
  const showToast = (msg, type = 'ok') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 3000); };

  const load = async () => {
    setLoading(true);
    try {
      const calls = [api.getClients(false, showInactive), api.getPlans()];
      if (isAdmin) calls.push(api.getResellers());
      const [c, p, r] = await Promise.all(calls);
      setClients(c);
      setPlans(p);
      if (r) setResellers(r);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [showInactive]);

  const myCompanyId = isReseller ? me?.client_id : null;
  const canCreate = canCreateNormal;
  const enriched = clients.map(c => ({ ...c, plan_name: c.plan?.name || '' }));
  const filtered = enriched.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.city || '').toLowerCase().includes(search.toLowerCase())
  );
  const { sorted: sortedAll, sortKey, sortDir, toggle } = useSortable(filtered, 'name', 'asc');
  // #210: al reseller le sale primero su empresa
  const sorted = isReseller ? [...sortedAll.filter(c => c.id === myCompanyId), ...sortedAll.filter(c => c.id !== myCompanyId)] : sortedAll;

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>;

  return (
    <div>
      {modal && <NewClientModal plans={plans} resellers={resellers} isAdmin={isAdmin} onClose={() => setModal(false)} onCreated={load} />}
      {hardDeleteTarget && (
        <HardDeleteClientModal
          client={hardDeleteTarget}
          onClose={() => setHardDeleteTarget(null)}
          onSuccess={() => {
            const name = hardDeleteTarget.name;
            setHardDeleteTarget(null);
            showToast(`Cliente ${name} eliminado definitivamente`);
            load();
          }}
        />
      )}

      {toast.msg && (
        <div style={{ marginBottom: 12, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12, background: toast.type === 'ok' ? 'rgba(0,229,160,0.1)' : 'rgba(255,77,109,0.1)', border: `1px solid ${toast.type === 'ok' ? 'var(--green)' : 'var(--red)'}`, color: toast.type === 'ok' ? 'var(--green)' : 'var(--red)' }}>
          {toast.type === 'ok' ? '✓' : '✕'} {toast.msg}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-end', justifyContent: 'space-between', marginBottom: 24, gap: isMobile ? 12 : 0 }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>Clientes</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{clients.filter(c => c.active).length} clientes activos{showInactive && clients.some(c => !c.active) ? ` · ${clients.filter(c => !c.active).length} inactivos` : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="buscar cliente..."
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 14px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', flex: 1, minWidth: 0 }} />
          {canCreate && <button onClick={() => setModal(true)} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '8px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700, letterSpacing: 1, whiteSpace: 'nowrap' }}>+ NUEVO</button>}
        </div>
      </div>

      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--red)',
            padding: 32, maxWidth: 420,
          }}>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 13,
              color: 'var(--red)', marginBottom: 16,
            }}>
              ELIMINAR CLIENTE
            </div>
            <p style={{ fontSize: 13, marginBottom: 8 }}>
              ¿Eliminar a <strong>{confirmDelete.name}</strong>?
            </p>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 24 }}>
              Esta acción desactiva el cliente. Los servidores y jobs
              asociados no se eliminan. El bucket de Wasabi NO se borra
              automáticamente.
            </p>
            {confirmDelete.is_reseller && (
              <p style={{ fontSize: 12, color: 'var(--yellow)', marginBottom: 24 }}>
                Es una empresa reseller: sus clientes pasan a OpenBackup (sin reseller) y reactivarla no los devuelve.
              </p>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deletingClient}
                style={{
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--muted)', padding: '8px 20px',
                  fontFamily: 'var(--mono)', fontSize: 12, cursor: deletingClient ? 'not-allowed' : 'pointer',
                }}
              >
                CANCELAR
              </button>
              <button
                onClick={async () => {
                  setDeletingClient(true);
                  try {
                    await api.deleteClient(confirmDelete.id);
                    setConfirmDelete(null);
                    load();
                  } catch (e) { alert(e.message); }
                  finally { setDeletingClient(false); }
                }}
                disabled={deletingClient}
                style={{
                  background: 'var(--red)', border: 'none',
                  color: 'white', padding: '8px 20px',
                  fontFamily: 'var(--mono)', fontSize: 12,
                  fontWeight: 700, cursor: deletingClient ? 'not-allowed' : 'pointer',
                  opacity: deletingClient ? 0.6 : 1,
                }}
              >
                {deletingClient ? 'ELIMINANDO...' : 'CONFIRMAR ELIMINACIÓN'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resellerModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            padding: 32, minWidth: 400,
          }}>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 12,
              letterSpacing: 2, color: 'var(--muted)',
              textTransform: 'uppercase', marginBottom: 20,
            }}>
              Asignar Reseller
            </div>
            <p style={{ fontSize: 13, marginBottom: 16 }}>
              Cliente: <strong>{resellerModal.name}</strong>
            </p>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
              Reseller actual: {resellerModal.reseller_name || '— ninguno —'}
            </p>
            <label style={{
              display: 'block', fontSize: 10, letterSpacing: 2,
              color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 6,
            }}>
              NUEVO RESELLER
            </label>
            <select
              value={selectedReseller}
              onChange={e => setSelectedReseller(e.target.value)}
              disabled={!!resellerModal.is_reseller}
              style={{
                width: '100%', background: 'var(--bg)',
                border: '1px solid var(--border)', color: 'var(--text)',
                padding: '8px 12px', fontFamily: 'var(--mono)',
                fontSize: 13, marginBottom: 24, outline: 'none',
              }}
            >
              <option value=''>— Sin reseller —</option>
              {resellers.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {resellerModal.is_reseller && (
              <p style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', margin: '-16px 0 24px' }}>
                Es una empresa reseller: no puede tener reseller (un solo nivel).
              </p>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setResellerModal(null); setSelectedReseller(''); }}
                style={{
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--muted)', padding: '8px 20px',
                  fontFamily: 'var(--mono)', fontSize: 12, cursor: 'pointer',
                }}
              >
                CANCELAR
              </button>
              <button
                onClick={async () => {
                  try {
                    // #198 parte 2 — el PUT combina con lo guardado: sólo name
                    // (obligatorio) y el campo que cambia, no el objeto de la lista.
                    await api.updateClient(resellerModal.id, {
                      name: resellerModal.name,
                      reseller_id: selectedReseller || null,
                    });
                    setResellerModal(null);
                    setSelectedReseller('');
                    load();
                  } catch (e) { alert(e.message); }
                }}
                style={{
                  background: 'var(--accent)', border: 'none',
                  color: 'var(--bg)', padding: '8px 20px',
                  fontFamily: 'var(--mono)', fontSize: 12,
                  fontWeight: 700, cursor: 'pointer',
                }}
              >
                GUARDAR
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdmin && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 12, color: 'var(--muted)', cursor: 'pointer', userSelect: 'none', fontFamily: 'var(--mono)' }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            style={{ cursor: 'pointer', accentColor: 'var(--accent)' }}
          />
          Mostrar también clientes desactivados
        </label>
      )}

      {filtered.length === 0 ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
          {search ? 'Sin resultados' : 'No hay clientes todavía. ¡Creá el primero!'}
        </div>
      ) : isMobile ? (
        <div>
          {sorted.map(c => {
            const pct = (c.storage_used_gb / (c.plan?.included_storage_gb || 1)) * 100;
            const barColor = pct > 80 ? 'var(--yellow)' : 'var(--accent)';
            const status = clientStatus(c);
            return (
              <div key={c.id} onClick={() => navigate(`/clients/${c.id}`)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 8, cursor: 'pointer', opacity: c.active ? 1 : 0.7 }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>CLIENTE</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {c.name}
                  <ClientBadge client={c} myCompanyId={myCompanyId} />
                  {!c.active && (
                    <span style={{ padding: '1px 6px', background: 'rgba(156,163,175,0.15)', color: '#9ca3af', border: '1px solid #9ca3af', borderRadius: 4, fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 1 }}>INACTIVO</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>{c.city || '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>PLAN</div>
                <div style={{ fontSize: 13, marginBottom: 10 }}>
                  <Badge bg='rgba(0,212,255,0.12)' color='var(--accent)' label={c.plan?.name || '—'} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>STORAGE</div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, marginBottom: 4 }}>{c.storage_used_gb} / {c.plan?.included_storage_gb ?? '?'} GB</div>
                  <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: barColor, borderRadius: 2 }} />
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>ESTADO</div>
                <div style={{ marginBottom: 10 }}>
                  <Badge
                    bg={status === 'alert' ? 'rgba(255,77,109,0.12)' : 'rgba(0,229,160,0.12)'}
                    color={status === 'alert' ? 'var(--red)' : 'var(--green)'}
                    label={status === 'alert' ? 'ALERTA' : 'OK'} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button onClick={e => { e.stopPropagation(); navigate(`/clients/${c.id}`); }}
                    style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>VER</button>
                  {isAdmin && c.active && resellers.length > 0 && (
                    <button onClick={e => { e.stopPropagation(); setSelectedReseller(c.reseller_id || ''); setResellerModal(c); }}
                      style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>RESELLER</button>
                  )}
                  {isAdmin && c.active && (
                    <button onClick={e => { e.stopPropagation(); setConfirmDelete(c); }}
                      style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>ELIMINAR</button>
                  )}
                  {isAdmin && !c.active && (
                    <>
                      <button onClick={async e => { e.stopPropagation(); setReactivatingId(c.id); try { await api.reactivateClient(c.id); showToast(`Cliente ${c.name} reactivado`); load(); } catch (err) { showToast(err.message, 'error'); } finally { setReactivatingId(null); } }}
                        disabled={reactivatingId === c.id}
                        style={{ background: 'transparent', border: '1px solid var(--green)', color: 'var(--green)', padding: '5px 10px', fontSize: 11, cursor: reactivatingId === c.id ? 'not-allowed' : 'pointer', fontFamily: 'var(--mono)', opacity: reactivatingId === c.id ? 0.5 : 1 }}>{reactivatingId === c.id ? '...' : 'REACTIVAR'}</button>
                      <button onClick={e => { e.stopPropagation(); setHardDeleteTarget(c); }}
                        style={{ background: 'transparent', border: '1px solid var(--red)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', fontWeight: 700 }}>ELIM. DEF.</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg)' }}>
                {[
                  ['Cliente', 'name'],
                  ['Plan',    'plan_name'],
                  ['Storage', 'storage_used_gb'],
                ].map(([label, col]) => (
                  <SortTh key={col} label={label} colKey={col} sortKey={sortKey} sortDir={sortDir} onToggle={toggle}
                    style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }} />
                ))}
                {['Reseller', 'Dirección', 'Estado', ''].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((c, i) => {
                const pct = (c.storage_used_gb / (c.plan?.included_storage_gb || 1)) * 100;
                const barColor = pct > 80 ? 'var(--yellow)' : 'var(--accent)';
                const status = clientStatus(c);
                return (
                  <tr key={c.id} onClick={() => navigate(`/clients/${c.id}`)}
                    style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer', opacity: c.active ? 1 : 0.7 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {c.name}
                        <ClientBadge client={c} myCompanyId={myCompanyId} />
                        {!c.active && (
                          <span style={{ padding: '1px 6px', background: 'rgba(156,163,175,0.15)', color: '#9ca3af', border: '1px solid #9ca3af', borderRadius: 4, fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 1 }}>INACTIVO</span>
                        )}
                      </div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{c.city || '—'}</div>
                      {isAdmin && c.bucket_name && (
                        <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--muted)', marginTop: 2 }}>{c.bucket_name}</div>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <Badge bg='rgba(0,212,255,0.12)' color='var(--accent)' label={c.plan?.name || '—'} />
                    </td>
                    <td style={{ padding: '14px 16px', minWidth: 140 }}>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{c.storage_used_gb} / {c.plan?.included_storage_gb ?? '?'} GB</div>
                      <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, overflow: 'hidden', marginTop: 4 }}>
                        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: barColor, borderRadius: 2 }} />
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: 'var(--muted)' }}>
                      {c.reseller_name || '—'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: 'var(--muted)' }}>{c.address || '—'}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <Badge
                        bg={status === 'alert' ? 'rgba(255,77,109,0.12)' : 'rgba(0,229,160,0.12)'}
                        color={status === 'alert' ? 'var(--red)' : 'var(--green)'}
                        label={status === 'alert' ? 'ALERTA' : 'OK'} />
                    </td>
                    <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                      <button onClick={e => { e.stopPropagation(); navigate(`/clients/${c.id}`); }}
                        style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>VER</button>
                      {isAdmin && c.active && resellers.length > 0 && (
                        <button onClick={e => { e.stopPropagation(); setSelectedReseller(c.reseller_id || ''); setResellerModal(c); }}
                          style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', marginLeft: 4 }}>
                          RESELLER
                        </button>
                      )}
                      {isAdmin && c.active && (
                        <button onClick={e => { e.stopPropagation(); setConfirmDelete(c); }}
                          style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', marginLeft: 4 }}>
                          ELIMINAR
                        </button>
                      )}
                      {isAdmin && !c.active && (
                        <>
                          <button onClick={async e => { e.stopPropagation(); setReactivatingId(c.id); try { await api.reactivateClient(c.id); showToast(`Cliente ${c.name} reactivado`); load(); } catch (err) { showToast(err.message, 'error'); } finally { setReactivatingId(null); } }}
                            disabled={reactivatingId === c.id}
                            style={{ background: 'transparent', border: '1px solid var(--green)', color: 'var(--green)', padding: '5px 10px', fontSize: 11, cursor: reactivatingId === c.id ? 'not-allowed' : 'pointer', fontFamily: 'var(--mono)', marginLeft: 4, opacity: reactivatingId === c.id ? 0.5 : 1 }}>
                            {reactivatingId === c.id ? '...' : 'REACTIVAR'}
                          </button>
                          <button onClick={e => { e.stopPropagation(); setHardDeleteTarget(c); }}
                            style={{ background: 'transparent', border: '1px solid var(--red)', color: 'var(--red)', padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', marginLeft: 4, fontWeight: 700 }}>
                            ELIM. DEF.
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

